<template>
  <!-- 补拍动画帧向导：选页 → 逐页真屏采集（连拍画面+内录音）→ 修剪帧 → 确认写入 -->
  <el-dialog
    title="补拍动画帧"
    :visible.sync="innerVisible"
    width="860px"
    top="5vh"
    :close-on-click-modal="false"
    :before-close="onBeforeClose"
    @open="resetState"
  >
    <el-steps :active="step" finish-status="success" align-center class="recap-steps">
      <el-step title="选择页码" />
      <el-step title="逐页采集" />
      <el-step title="完成" />
    </el-steps>

    <!-- 第 1 步：选页 -->
    <div v-if="step === 0" class="recap-pane">
      <div class="recap-tip">
        适用于「单击交互页」等自动转换出不来动画的页：真屏连拍画面 + 内录声音，替换该页的动画帧序列。
        推荐选择原始 PPT 文件用「一键采集」（自动全屏放映，画面干净）；也可不选，改用手动窗口放映。支持单页或连续页码范围。
      </div>
      <div class="recap-row">
        <span class="recap-label">页码范围：从第</span>
        <el-input-number
          v-model="startPage"
          :min="1"
          :max="total"
          size="small"
          controls-position="right"
          style="width: 110px"
          @keyup.enter.native="beginQueue"
        />
        <span class="recap-label">页 到第</span>
        <el-input-number
          v-model="endPage"
          :min="1"
          :max="total"
          size="small"
          controls-position="right"
          style="width: 110px"
          @keyup.enter.native="beginQueue"
        />
        <span class="recap-label">页</span>
        <span class="recap-hint">共 {{ total }} 页，范围 1 ~ {{ total }}</span>
      </div>
      <div class="recap-row">
        <span class="recap-label">原始 PPT 文件：</span>
        <input ref="pptxInput" type="file" accept=".pptx" style="display: none" @change="onPptxChange">
        <el-button size="small" icon="el-icon-document" @click="$refs.pptxInput.click()">{{ pptxFile ? '重新选择' : '选择 pptx' }}</el-button>
        <span v-if="pptxFile" class="recap-hint">{{ pptxFile.name }}（一键采集：自动全屏放映）</span>
        <span v-else class="recap-hint">可不选，不选则按手动窗口放映操作</span>
      </div>
      <div class="recap-actions">
        <el-button size="small" @click="innerVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :disabled="!parseRange" @click="beginQueue">开 始</el-button>
      </div>
    </div>

    <!-- 第 2 步：逐页采集 -->
    <div v-else-if="step === 1" class="recap-pane">
      <div class="recap-head">
        当前第 {{ qi + 1 }} 页 / 共 {{ queue.length }} 页<span v-if="curPage">（PPT 第 {{ curPage.page }} 页）</span>
      </div>
      <ol v-if="pptxFile" class="recap-guide">
        <li>点击下方【开始采集】，系统会自动全屏放映第 {{ curPage ? curPage.page : '' }} 页（约 2-4 秒，期间请勿操作鼠标键盘、不要说话）；</li>
        <li>放映出现后，按正常上课节奏点击页面上的交互；</li>
        <li>讲解结束后按 Esc 退出放映，回到本窗口点【完成采集】；</li>
        <li>放映弹出约 2 秒后才开始录屏；若开头仍有个别废帧，下一步用滑块掐头去掉即可。</li>
      </ol>
      <ol v-else class="recap-guide">
        <li>打开原始 PPT 文件，定位到第 {{ curPage ? curPage.page : '' }} 页；</li>
        <li>先设置一次窗口放映（之后不用再设）：菜单【幻灯片放映 → 设置幻灯片放映】，放映类型选「观众自行浏览(窗口)」并确定——这样放映不会全屏盖住本弹窗；</li>
        <li>菜单【幻灯片放映 → 从当前页开始】进入放映；</li>
        <li>点击下方【开始采集】后，按正常上课节奏点击页面上的交互；</li>
        <li>讲解结束后点【完成采集】。</li>
      </ol>

      <div class="recap-actions">
        <el-button v-if="!capturing" size="small" type="primary" icon="el-icon-video-camera" :loading="openingSlide" @click="startCapture">{{ openingSlide ? '正在唤起全屏放映…' : '开始采集' }}</el-button>
        <template v-else>
          <span class="recap-live"><i class="recap-dot" />正在采集，请按正常上课节奏点击页面上的交互…</span>
          <el-button size="small" type="success" @click="stopCapture">完成采集</el-button>
        </template>
      </div>

      <!-- 采集结果：胶片条 + 掐头去尾滑块 -->
      <template v-if="frames.length">
        <div class="recap-film" v-loading="thumbsLoading">
          <div
            v-for="(f, i) in frames"
            :key="f.i"
            class="recap-frame"
            :class="{ off: i < selRange[0] || i > selRange[1] }"
          >
            <img v-if="thumbUrls[f.i]" :src="thumbUrls[f.i]" :alt="'第' + f.i + '帧'">
            <div v-else class="recap-frame-ph">加载中…</div>
            <span class="recap-frame-no">{{ f.i }}</span>
          </div>
        </div>
        <div class="recap-slider">
          <el-slider :value="selRange" range :min="0" :max="frames.length - 1" :show-tooltip="false" @input="onRangeInput" />
          <span class="recap-count">
            已选 {{ selRange[1] - selRange[0] + 1 }} / 共 {{ frames.length }} 帧<span v-if="durMs">（全程 {{ (durMs / 1000).toFixed(1) }} 秒）</span>
          </span>
        </div>
        <div class="recap-audio">
          <el-tag v-if="audio" size="mini" type="success">已录到声音</el-tag>
          <el-tag v-else size="mini" type="warning">未录到声音</el-tag>
        </div>
      </template>

      <div class="recap-hint recap-act-hint">
        逐页处理、互不影响：【确认并下一页】= 把选中的帧和录音写入第 {{ curPage ? curPage.page : '' }} 页；
        【放弃第 {{ curPage ? curPage.page : '' }} 页】= 只丢弃这一页本次采集的帧，已写入的其他页和还没采集的页都不受影响。
      </div>
      <div class="recap-actions">
        <el-button size="small" type="danger" plain :disabled="committing || capturing" @click="skipPage">放弃第 {{ curPage ? curPage.page : '' }} 页</el-button>
        <el-button
          v-if="frames.length"
          size="small"
          type="primary"
          :disabled="selCount < 2"
          :loading="committing"
          @click="commitPage"
        >确认并下一页</el-button>
      </div>
    </div>

    <!-- 第 3 步：完成 -->
    <div v-else class="recap-pane">
      <el-table :data="queue" size="small" border max-height="360">
        <el-table-column prop="page" label="页码" width="100" />
        <el-table-column label="结果">
          <template slot-scope="{ row }">
            <el-tag v-if="row.status === 'done'" size="mini" type="success">已写入</el-tag>
            <el-tag v-else size="mini" type="info">已跳过</el-tag>
          </template>
        </el-table-column>
      </el-table>
      <div class="recap-tip recap-done-tip">全部写入完成，可直接去预览页查看效果。</div>
      <div class="recap-actions">
        <el-button size="small" type="primary" @click="innerVisible = false">完 成</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { recaptureStart, recaptureStop, recaptureCommit, recaptureFrameUrl, recaptureOpenSlide } from '@/api/ppt'
import http from '@/utils/http'

export default {
  name: 'RecaptureDialog',
  props: {
    value: { type: Boolean, default: false },
    projectId: { type: [Number, String], default: null },
    total: { type: Number, default: 0 },
    currentPage: { type: Number, default: 1 }
  },
  data() {
    return {
      step: 0,          // 0 选页 / 1 逐页采集 / 2 完成
      startPage: null,  // 起始页码
      endPage: null,    // 终止页码
      queue: [],        // [{ page, status: pending/done/skipped }]
      qi: 0,
      capturing: false,
      frames: [],       // recaptureStop 返回 [{i,t}]
      audio: false,
      durMs: 0,
      selRange: [0, 0], // 滑块选中区间（帧数组下标，含两端）
      thumbUrls: {},    // 帧序号 i -> objectURL（axios 拉 blob 生成）
      thumbsLoading: false,
      committing: false,
      pptxFile: null,     // 原始 pptx（一键采集用；选一次，本批次所有页共用）
      pptxUploaded: false, // 本批次是否已把 pptxFile 传到后端缓存（之后各页复用，不再重复上传）
      openingSlide: false, // 正在等待后端唤起全屏放映
      openSeq: 0           // 唤起放映/延迟开录的凭证序号，翻页递增使旧定时器失效
    }
  },
  computed: {
    innerVisible: {
      get() { return this.value },
      set(v) { this.$emit('input', v) }
    },
    curPage() {
      return this.queue[this.qi] || null
    },
    selCount() {
      return this.selRange[1] - this.selRange[0] + 1
    },
    // 解析页码范围：校验为 1..total 的整数且起始页 ≤ 终止页；合法返回页码数组（起止相同为单页），非法返回 null
    parseRange() {
      const a = this.startPage
      const b = this.endPage
      if (!Number.isInteger(a) || !Number.isInteger(b)) return null
      if (a < 1 || b > this.total || a > b) return null
      const out = []
      for (let n = a; n <= b; n++) out.push(n)
      return out
    }
  },
  methods: {
    // 打开对话框时重置全部状态
    resetState() {
      this.step = 0
      this.startPage = this.currentPage || 1
      this.endPage = this.currentPage || 1
      this.queue = []
      this.qi = 0
      this.resetCapture()
    },
    // 每页一轮：清空上一页的采集结果
    resetCapture() {
      this.releaseThumbs()
      this.capturing = false
      this.openingSlide = false
      this.openSeq++ // 使上一页可能挂起的"2 秒后开录"定时器失效
      this.frames = []
      this.audio = false
      this.durMs = 0
      this.selRange = [0, 0]
    },
    beginQueue() {
      const list = this.parseRange
      if (!list) {
        this.$message.warning('请填写正确的页码：1-' + this.total + '，且起始页不能大于终止页')
        return
      }
      this.queue = list.map((page) => ({ page, status: 'pending' }))
      this.qi = 0
      this.resetCapture()
      this.step = 1
    },
    // 开始采集：一键采集=先唤起全屏放映、弹出 2 秒后再录屏（否则等待期废帧太多）；
    // 手动模式（未选 pptx）用户已在放映中，立即开始
    startCapture() {
      if (this.pptxFile) {
        this.openSlideAuto(true)
      } else {
        this.$message.info('未选择 PPT 文件，请按上方手动方式（窗口放映）操作')
        this.doStartCapture()
      }
    },
    doStartCapture() {
      recaptureStart(this.projectId, this.curPage.page)
        .then((res) => {
          if (res && res.data && res.data.code === 0) {
            // 重新开始采集时丢弃旧的帧，避免和本次结果混在一起
            this.releaseThumbs()
            this.frames = []
            this.selRange = [0, 0]
            this.capturing = true
          } else {
            this.$message.error('开始采集失败：' + ((res && res.data && res.data.message) || '未知错误'))
          }
        })
        .catch(() => {})
    },
    // 一键采集：后端缓存 pptx（首次上传）并 COM 唤起指定页全屏放映；
    // startAfter=true 时放映弹出 2 秒后再开始录屏
    openSlideAuto(startAfter) {
      this.openingSlide = true
      // 递增序号作为"2 秒后开录"定时器的凭证：翻页/重开会话时旧定时器自动失效
      //（不能用 openingSlide 做凭证——它在接口返回时就被 finally 清掉，定时器触发时必然为 false）
      const token = ++this.openSeq
      recaptureOpenSlide(this.projectId, this.curPage.page, this.pptxUploaded ? null : this.pptxFile)
        .then((res) => {
          if (res && res.data && res.data.code === 0) {
            this.pptxUploaded = true
            if (startAfter) {
              setTimeout(() => {
                // 弹窗已关闭或翻到了别的页（序号变了）则不再开录
                if (this.innerVisible && this.openSeq === token) {
                  this.doStartCapture()
                }
              }, 2000)
            }
          } else {
            this.$message.error('自动全屏放映失败：' + ((res && res.data && res.data.message) || '未知错误') + '；可改为手动窗口放映继续')
          }
        })
        .catch(() => {
          this.$message.error('自动全屏放映请求失败，可改为手动窗口放映继续')
        })
        .finally(() => { this.openingSlide = false })
    },
    // 第 1 步选择原始 pptx
    onPptxChange(e) {
      const f = e.target.files && e.target.files[0]
      if (!f) return
      if (!/\.pptx$/i.test(f.name)) {
        this.$message.warning('请选择 .pptx 文件')
        e.target.value = ''
        return
      }
      this.pptxFile = f
      this.pptxUploaded = false
    },
    stopCapture() {
      recaptureStop(this.projectId, this.curPage.page)
        .then((res) => {
          const d = (res && res.data && res.data.data) || {}
          this.capturing = false
          this.frames = Array.isArray(d.frames) ? d.frames : []
          this.audio = !!d.audio
          this.durMs = d.durMs || 0
          if (!this.frames.length) {
            this.$message.warning('未采集到任何帧，请重新采集或跳过此页')
            return
          }
          this.selRange = [0, this.frames.length - 1]
          this.loadThumbs()
        })
        .catch(() => { this.capturing = false })
    },
    // 帧间距守卫：el-slider(range) 发出的是排序后的 [min,max]，但组件本身不防两柄交叉/重合，
    // 重合后只能抓到上层柄（表现为"滑不动"）。这里强制间距≥1：拖近或拖过另一柄时钳制在
    // 距另一柄 1 帧处，选区始终 ≥2 帧（与"确认并下一页"的 selCount≥2 约束一致）
    onRangeInput(val) {
      if (!Array.isArray(val) || val.length < 2) return
      const old = this.selRange
      if (val[1] - val[0] >= 1) {
        this.selRange = val
        return
      }
      if (val[0] !== old[0] && val[1] === old[1]) {
        this.selRange = [Math.max(0, val[1] - 1), val[1]]
      } else if (val[1] !== old[1] && val[0] === old[0]) {
        this.selRange = [val[0], Math.min(this.frames.length - 1, val[0] + 1)]
      } else {
        this.selRange = [...old]
      }
    },
    // 帧图走 Authorization 头鉴权，<img> 带不上请求头，统一用 axios 拉 arraybuffer → Blob → objectURL
    loadThumbs() {
      this.thumbsLoading = true
      const page = this.curPage.page
      Promise.all(this.frames.map((f) => {
        return http.get(recaptureFrameUrl(this.projectId, page, f.i), null, true)
          .then((res) => {
            const type = (res.headers && res.headers['content-type']) || 'image/jpeg'
            this.$set(this.thumbUrls, f.i, URL.createObjectURL(new Blob([res.data], { type })))
          })
          .catch(() => {})
      })).finally(() => { this.thumbsLoading = false })
    },
    releaseThumbs() {
      Object.keys(this.thumbUrls).forEach((k) => {
        try { URL.revokeObjectURL(this.thumbUrls[k]) } catch (e) { /* 忽略 */ }
      })
      this.thumbUrls = {}
    },
    commitPage() {
      const page = this.curPage.page
      const sel = this.frames.slice(this.selRange[0], this.selRange[1] + 1).map((f) => f.i)
      const doCommit = () => {
        this.committing = true
        recaptureCommit(this.projectId, page, sel, this.audio)
          .then((res) => {
            if (res && res.data && res.data.code === 0) {
              this.curPage.status = 'done'
              // 通知父组件同步本地页数据（后端 commit 已即时落库）
              this.$emit('applied', { page, data: res.data.data })
              this.$message.success('第 ' + page + ' 页已写入补拍结果')
              this.nextPage()
            } else {
              this.$message.error('写入失败：' + ((res && res.data && res.data.message) || '未知错误'))
            }
          })
          .catch(() => {})
          .finally(() => { this.committing = false })
      }
      if (!this.audio) {
        this.$confirm('未录到声音，确定继续？', '提示', { type: 'warning' }).then(doCommit).catch(() => {})
      } else {
        doCommit()
      }
    },
    // 放弃本页：已采集到帧/录音时先二次确认，防止误点把采集结果全部丢弃
    skipPage() {
      const doSkip = () => {
        this.curPage.status = 'skipped'
        this.nextPage()
      }
      if (this.frames.length) {
        const page = this.curPage.page
        this.$confirm(
          '只放弃第 ' + page + ' 页：本次采集的 ' + this.frames.length + ' 帧和录音将被丢弃（不可恢复），其他页不受影响（已写入的还在，未采集的继续），确定放弃第 ' + page + ' 页吗？',
          '放弃第 ' + page + ' 页',
          { type: 'warning', confirmButtonText: '确定放弃第 ' + page + ' 页', cancelButtonText: '再想想' }
        ).then(doSkip).catch(() => {})
      } else {
        doSkip()
      }
    },
    nextPage() {
      if (this.qi + 1 >= this.queue.length) {
        this.step = 2
        return
      }
      this.qi++
      this.resetCapture()
    },
    // 采集中禁止直接关窗：confirm 拦截，确认后先通知后端停止采集再关闭
    onBeforeClose(done) {
      if (!this.capturing) {
        done()
        return
      }
      this.$confirm('正在采集中，关闭将中止本次采集（不会写入任何数据），确定关闭吗？', '提示', { type: 'warning' })
        .then(() => {
          recaptureStop(this.projectId, this.curPage.page).catch(() => {})
          this.capturing = false
          done()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.recap-steps {
  margin-bottom: 18px;
}
.recap-pane {
  min-height: 260px;
}
.recap-tip {
  font-size: 12px;
  color: #666;
  line-height: 1.8;
  background: #f7f8fa;
  border-radius: 6px;
  padding: 10px 12px;
}
.recap-done-tip {
  margin-top: 12px;
}
.recap-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;
}
.recap-label {
  font-size: 13px;
  color: #333;
}
.recap-hint {
  font-size: 12px;
  color: #909399;
}
.recap-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.recap-head {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
}
.recap-guide {
  margin: 0 0 4px;
  padding-left: 18px;
  font-size: 13px;
  color: #333;
  line-height: 2;
}
/* 采集中：文字+圆点呼吸效果 */
.recap-live {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #f56c6c;
  font-size: 14px;
  font-weight: 600;
  animation: recap-breathe 1.4s ease-in-out infinite;
}
.recap-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f56c6c;
}
@keyframes recap-breathe {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
/* 胶片条：横向滚动缩略图，区间外帧半透明 */
.recap-film {
  position: relative; /* v-loading 遮罩以本元素为界，避免盖住下方滑块 */
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px;
  background: #141824;
  border-radius: 6px;
  margin-top: 12px;
}
.recap-frame {
  position: relative;
  flex-shrink: 0;
  width: 96px;
  border-radius: 4px;
  overflow: hidden;
  transition: opacity 0.15s;
}
.recap-frame.off {
  opacity: 0.35;
}
.recap-frame img {
  display: block;
  width: 100%;
}
.recap-frame-ph {
  width: 96px;
  height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 12px;
  background: #2b2f36;
}
.recap-frame-no {
  position: absolute;
  right: 2px;
  top: 2px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  padding: 0 5px;
  border-radius: 8px;
}
.recap-slider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 10px;
}
.recap-slider .el-slider {
  flex: 1;
}
.recap-count {
  flex-shrink: 0;
  font-size: 12px;
  color: #666;
}
.recap-audio {
  margin-top: 6px;
}
.recap-act-hint {
  margin-top: 14px;
}
</style>
